// Alfred auto gen const name
import { IOrder, ISubOrder } from 'types/order';
import { PayType } from 'config/order';
import { BASE_AFTERSALE } from './button-status';
import { AFTERSALE_TYPE } from 'pages/newaftersale/detail/constants';
import {
    isVoucherOrder,
    getYushiOrderStatus,
    isOnlineRefundOrderNeedPayee,
    isOnlineRefundFailOrder
} from './utils';

const IN_RETURN_FOR = {
    className: 'processing',
    title: '退货申请中',
    subTitle: '待商家处理'
};

const IN_PROCESSING_SERVICE = {
    className: 'processing',
    title: '客服处理中'
};

const FOR_REFUND = {
    className: 'processing',
    title: '待退货',
    subTitle: '请填写物流'
};

const HAVE_TO_RETURN = {
    className: 'processing',
    title: '已退货',
    subTitle: '待商家处理'
};

const MERCHANTS_HAVE_AGREED_TO = {
    className: 'processing',
    title: '商家已同意'
};

const CUSTOMER_HAS_AGREED_TO = {
    className: 'processing',
    title: '客服已同意'
};

const RETURN_HAS_BEEN_CANCELLED = {
    className: 'close',
    title: '退货已取消'
};

const REFUND_SUCCESS = {
    className: 'success',
    title: '退款成功'
};

const REFUND_FAILURE = {
    className: 'reject',
    title: '退款失败'
};

const MERCHANT_HAS_REFUSED_TO = {
    className: 'reject',
    title: '商家已拒绝',
    subTitle: '可申请客服申诉'
};

const VOUCHER_REFUND_FAILED = {
    className: 'reject',
    title: '退款失败'
};

const VOUCHER_REFUND_FAILED_CAN_APPLY = {
    className: 'reject',
    title: '退款失败',
    subTitle: '可重新申请'
};

const PRE_MERCHANT_HAS_REFUSED_TO = {
    className: 'reject',
    title: '商家已拒绝'
};

const CUSTOMER_SERVICE_HAS_REFUSED_TO = {
    className: 'reject',
    title: '客服已拒绝'
};

const REFUND_APPLICATION = {
    className: 'processing',
    title: '退款申请中',
    subTitle: '待商家处理'
};

const REFUND_HAS_BEEN_CANCELLED = {
    className: 'close',
    title: '退款已取消'
};

const REFUND_HAS_AGREED_TO = {
    className: 'processing',
    title: '退款已同意'
};

const REFUND_FAILURE_AUTO = {
    title: '退款失败',
    subTitle: '请联系官方客服'
};

const REFUND_FAILURE_NEED_PAYEE = {
    title: '退款失败',
    subTitle: '请修改收款信息'
};

// 新增换货处理
const EXCHANGE_WAITING_FOR = {
    className: 'processing',
    title: '等待商家处理'
};
const EXCHANGE_HAVE_AGREED_TO = {
    className: 'processing',
    title: '商家已同意换货',
    subTitle: '请填写物流'
};
const EXCHANGE_HAS_REFUSED_TO = {
    className: 'reject',
    title: '商家拒绝换货',
    subTitle: '可申请客服申诉'
};
const EXCHANGE_CUSTOMER_HAS_AGREED_TO = {
    className: 'processing',
    title: '客服同意换货',
    subTitle: '请填写物流'
};
const EXCHANGE_CUSTOMER_HAS_AGREED_TO_RETURN_0 = {
    className: 'processing',
    title: '客服同意退款'
};
const EXCHANGE_CUSTOMER_HAS_AGREED_TO_RETURN_1 = {
    className: 'processing',
    title: '退款执行中'
};
const EXCHANGE_CUSTOMER_HAS_AGREED_TO_RETURN_2 = {
    className: 'success',
    title: '退款已完成'
};
const EXCHANGE_CUSTOMER_HAS_AGREED_TO_RETURN_3 = {
    className: 'close',
    title: '退款已关闭'
};
const EXCHANGE_CUSTOMER_SERVICE_HAS_REFUSED_TO = {
    className: 'reject',
    title: '客服已拒绝换货'
};
const EXCHANGE_APPLICATION = {
    className: 'processing',
    title: '等待商家处理'
};
const EXCHANGE_SEND_GOODS = {
    className: 'processing',
    title: '商家已发货'
};
const EXCHANGE_MERCHANT_HAS_REFUSED_TO = {
    className: 'reject',
    title: '商家拒绝发货',
    subTitle: '可申请客服申诉'
};
const EXCHANGE_CUSTOMER_AGREED_TO = {
    className: 'processing',
    title: '客服同意换货'
};
const EXCHANGE_HAS_BEEN_CANCELLED = {
    className: 'close',
    title: '换货已取消'
};

const EXCHANGE_SUCCESS = {
    className: 'success',
    title: '换货成功'
};

const EXCHANGE_BACK_RETURN = {
    className: 'success',
    title: '退款中'
};

const EXCHANGE_BACK_RETURN_SUCCESS = {
    className: 'success',
    title: '退款成功'
};

const EXCHANGE_FAILURE = {
    className: 'close',
    title: '换货关闭'
};

interface OrderDesc {
    className?: string;
    title: string;
    subTitle?: string;
}

function addNeedPayee(input: OrderDesc, needPayee: boolean): OrderDesc {
    return needPayee ? Object.assign(input, { subTitle: '请填写收款信息' }) : input;
}

export function getOrderDesc(order: ISubOrder): OrderDesc {
    const needPayee = isOnlineRefundOrderNeedPayee(order); // 是否需要填写收款信息
    const failType = isOnlineRefundFailOrder(order); // 退款失败状态
    // 退款失败
    const ONLINE_RERUND_FAIL =
        failType === 'SERVICE'
            ? REFUND_FAILURE_AUTO
            : failType === 'MODIFY'
            ? REFUND_FAILURE_NEED_PAYEE
            : '';
    // 商家状态：退款失败或商家同意
    const MERCHANTS_STATUS =
        ONLINE_RERUND_FAIL || addNeedPayee(MERCHANTS_HAVE_AGREED_TO, needPayee);
    // 客服状态：退款失败或客服同意
    const CUSTOMER_STATUS = ONLINE_RERUND_FAIL || addNeedPayee(CUSTOMER_HAS_AGREED_TO, needPayee);
    // 退货申请中
    const IN_RETURN = addNeedPayee(IN_RETURN_FOR, needPayee);
    // 退款申请中
    const IN_REFUND = addNeedPayee(REFUND_APPLICATION, needPayee);
    // 换货也依赖这个字段
    const status = order.final_status;

    let map: { [key: number]: OrderDesc };
    switch (order.aftersale_type) {
        case AFTERSALE_TYPE.Exchange:
            map = {
                40: EXCHANGE_WAITING_FOR,
                41: EXCHANGE_HAVE_AGREED_TO,
                45: EXCHANGE_HAS_REFUSED_TO,
                47: IN_PROCESSING_SERVICE,
                49: EXCHANGE_CUSTOMER_HAS_AGREED_TO,
                48: [
                    EXCHANGE_CUSTOMER_HAS_AGREED_TO_RETURN_0,
                    EXCHANGE_CUSTOMER_HAS_AGREED_TO_RETURN_1,
                    EXCHANGE_CUSTOMER_HAS_AGREED_TO_RETURN_2,
                    EXCHANGE_CUSTOMER_HAS_AGREED_TO_RETURN_3
                ][order.refund_status || 0],
                54: EXCHANGE_CUSTOMER_SERVICE_HAS_REFUSED_TO,
                42: EXCHANGE_APPLICATION,
                43: EXCHANGE_SEND_GOODS,
                46: EXCHANGE_MERCHANT_HAS_REFUSED_TO,
                50: IN_PROCESSING_SERVICE,
                44: EXCHANGE_SUCCESS,
                51: EXCHANGE_BACK_RETURN,
                52: EXCHANGE_BACK_RETURN_SUCCESS,
                53: EXCHANGE_HAS_BEEN_CANCELLED,
                55: EXCHANGE_FAILURE,
                56: EXCHANGE_CUSTOMER_AGREED_TO
            };
            break;
        default:
            map = {
                6: IN_RETURN,
                7: FOR_REFUND,
                8: IN_PROCESSING_SERVICE,
                9: REFUND_FAILURE,
                10: FOR_REFUND,
                11: HAVE_TO_RETURN,
                12: MERCHANTS_STATUS,
                13: IN_PROCESSING_SERVICE,
                14: CUSTOMER_STATUS,
                15: RETURN_HAS_BEEN_CANCELLED,
                16: IN_REFUND,
                17: REFUND_HAS_AGREED_TO,
                21: REFUND_SUCCESS,
                22: REFUND_SUCCESS,
                23: IN_RETURN,
                24: REFUND_SUCCESS,
                25: REFUND_HAS_BEEN_CANCELLED,
                26: PRE_MERCHANT_HAS_REFUSED_TO,
                27: MERCHANT_HAS_REFUSED_TO,
                28: CUSTOMER_SERVICE_HAS_REFUSED_TO,
                29: MERCHANT_HAS_REFUSED_TO,
                30: IN_REFUND,
                31: REFUND_HAS_BEEN_CANCELLED,
                32: MERCHANTS_STATUS,
                33: isVoucherOrder(order)
                    ? order.can_apply_aftersale
                        ? VOUCHER_REFUND_FAILED_CAN_APPLY
                        : VOUCHER_REFUND_FAILED
                    : MERCHANT_HAS_REFUSED_TO,
                34: IN_PROCESSING_SERVICE,
                35: CUSTOMER_STATUS,
                36: CUSTOMER_SERVICE_HAS_REFUSED_TO,
                37: REFUND_FAILURE,
                38: REFUND_SUCCESS,
                39: REFUND_SUCCESS
            };
            break;
    }

    if (status in map) {
        return map[status];
    }
    return {
        className: '',
        title: '',
        subTitle: ''
    };
}

function getYushiOrderDesc(type: number): string {
    switch (type) {
        case 2:
            return '质检中';
        case 3:
            return '鉴定通过';
        case 4:
            return '鉴定未通过';
        default:
            return '';
    }
}

export function getMainOrderDesc(order: IOrder): string {
    const shopOrderStatus = order.shop_order_status;
    const payType = order.pay_type;
    const yushiOrderStatus = getYushiOrderStatus(order);
    const yushiOrderDesc = getYushiOrderDesc(yushiOrderStatus);

    // 由于圈复杂度问题，把玉石类的状态单独提出
    if (yushiOrderDesc) {
        return yushiOrderDesc;
    }

    if (shopOrderStatus === 1 && (payType === PayType.WECHAT || payType === PayType.ALIPAY)) {
        return '待支付';
    }
    if (shopOrderStatus === 1 && payType === PayType.COD) {
        return '待确认';
    }
    if (shopOrderStatus === 2) {
        return isVoucherOrder(order) ? '待发券' : '待发货';
    }
    if (shopOrderStatus === 3) {
        return isVoucherOrder(order) ? '未使用' : '已发货';
    }
    if (shopOrderStatus === 4) {
        return '已取消';
    }
    if (shopOrderStatus === 5 && isVoucherOrder(order)) {
        return '已使用';
    }
    if (shopOrderStatus === 16) {
        return '退款申请中';
    }
    if (shopOrderStatus === 17) {
        return '退款已同意';
    }
    if (shopOrderStatus === 21) {
        return '退款成功';
    }
    if (shopOrderStatus === 25) {
        return '退款已取消';
    }
    if (shopOrderStatus === 26) {
        return '商家已拒绝';
    }
    if (shopOrderStatus === 101) {
        return '部分发货';
    }
    if (BASE_AFTERSALE.indexOf(shopOrderStatus) !== -1) {
        return '交易成功';
    }
    return order.order_status_str;
}

// 订单列表的子订单售后状态描述性文案
export const getSubOrderDesc = (subOrder: ISubOrder, shopOrder: IOrder) => {
    if (isPreRefundOrder(shopOrder.shop_order_status)) return;
    const desc = getOrderDesc(subOrder);
    return desc ? desc.title : '';
};

// 获取订单卡片售后相关信息
export function getOrderAfterSaleDesc(order: ISubOrder) {
    return getOrderDesc(order);
}

export function getPayTextDesc(order: IOrder) {
    const { final_status, pay_type } = order;
    if (pay_type === PayType.COD) {
        return '货到支付';
    }
    if (final_status === 1) {
        return '待支付';
    }
    if (final_status === 4) {
        return '未支付';
    }
    return '已支付';
}

// 是否是售前订单，此时是整单售后状态
export function isPreRefundOrder(status: number) {
    return [16, 17, 21, 25, 26].indexOf(status) !== -1;
}
